import React, { useState, useMemo, useCallback } from 'react';
import PropTypes from 'prop-types';
import AssetHierarchyDetailsCard from './AssetHierarchyDetailsCard';
import io from '../../io';
import { useQuery, useMutation, useSubscription } from 'hooks/apollo';
import { useTranslation } from 'react-i18next';
import { isURL } from 'utils/simple-validations';
import { useToast } from 'hooks/toasts/useToast';

const assetImageInitialState = {
  image: { url: null, saved: false, error: null },
  disabled: { saveAssetHierarchyImage: true, acquireAssetHierarchyImage: false }
};
const AssetHierarchyDetailsContainer = ({ hierarchy, onClose }) => {
  const [state, setState] = useState(assetImageInitialState);

  const addToast = useToast();
  const { t } = useTranslation();

  const validateURL = useCallback(url => {
    if (!url || url === '') return null;
    const { func, msg } = isURL();
    const valid = func(url);
    return valid ? null : msg;
  }, []);

  const setImageUrl = useCallback(
    (url, fieldCheck) => {
      const error = validateURL(url);
      setState(prev => {
        return fieldCheck
          ? {
              image: { ...prev.image, url, error },
              disabled: { ...prev.disabled, saveAssetHierarchyImage: !!error }
            }
          : { ...prev, image: { ...prev.image, url, error } };
      });
    },
    [setState, validateURL]
  );
  const disableAction = useCallback(name => setState(prev => ({ ...prev, disabled: { ...prev.disabled, [name]: true } })), [setState]);
  const enableAction = useCallback(name => setState(prev => ({ ...prev, disabled: { ...prev.disabled, [name]: false } })), [setState]);

  const { loading: hierarchyDataLoading, data: hierarchyData } = useQuery(io.queries.ASSET_HIERARCHY_DATA_QUERY, {
    variables: { hierarchyId: hierarchy.id },
    skip: !hierarchy.id,
    onCompleted: ({ assetHierarchyData }) => {
      const { imageUrl } = assetHierarchyData;
      setImageUrl(imageUrl);
    }
  });

  const [setAssetHierarchyImage] = useMutation(io.mutations.SET_ASSET_HIERARCHY_IMAGE);

  const [acquireAssetHierarchyImage] = useMutation(io.mutations.ACQUIRE_ASSET_HIERARCHY_IMAGE);

  const actions = useMemo(
    () => ({
      onClose,
      onAssetImageUrlChange: url => setImageUrl(url, true),
      saveAssetHierarchyImage: {
        onEffect: () => {
          if (state.image.error) return;
          disableAction('saveAssetHierarchyImage');
          setAssetHierarchyImage({ variables: { input: { hierarchyId: hierarchy.id, url: state.image.url } } });
        },
        disabled: state.disabled.saveAssetHierarchyImage
      },
      acquireAssetHierarchyImage: {
        onEffect: () => {
          disableAction('acquireAssetHierarchyImage');
          acquireAssetHierarchyImage({ variables: { input: { hierarchyId: hierarchy.id } } });
        },
        disabled: state.disabled.acquireAssetHierarchyImage
      }
    }),
    [onClose, hierarchy.id, state, setAssetHierarchyImage, acquireAssetHierarchyImage, setImageUrl, disableAction]
  );

  useSubscription(io.subscriptions.ASSET_HIERARCHY_IMAGE_SET, {
    skip: !hierarchy.id,
    variables: { id: hierarchy.id },
    onData: ({ data: { data }, client }) => {
      client.cache.evict({ id: `AssetHierarchyData:${data.assetHierarchyImageSet.id}` });
      client.cache.gc();
      setState(prev => ({ ...prev, image: { ...prev.image, saved: true } }));
      setTimeout(() => {
        setState(prev => ({ ...prev, image: { ...prev.image, saved: false } }));
      }, 1500);
    }
  });

  useSubscription(io.subscriptions.ASSET_HIERARCHY_IMAGE_ACQUIRED, {
    onData: ({ data: { data } }) => {
      const { url } = data.assetHierarchyImageAcquired;
      if (url) {
        setImageUrl(url);
      } else {
        addToast(t('AssetHierarchy.List.Actions.ViewDetails.NoImageFound'), 'warning');
      }
      enableAction('acquireAssetHierarchyImage');
    }
  });

  return (
    <AssetHierarchyDetailsCard
      hierarchy={{ header: hierarchy, data: { loading: hierarchyDataLoading, ...hierarchyData?.assetHierarchyData } }}
      image={{ ...state.image }}
      actions={actions}
    />
  );
};

AssetHierarchyDetailsContainer.propTypes = {
  hierarchy: PropTypes.object.isRequired,
  onClose: PropTypes.func.isRequired
};

export default AssetHierarchyDetailsContainer;
